import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, Card, Button, Input, Select } from '@pilot-hub/ui'
import { api } from '@pilot-hub/api-client'

// Exporte nach EGRED 2 (Anlage VII Muster-Flugbuch): Flugbuch Pilot, Flugbuch Drohne, Akku-Wartungsbuch

interface Option {
  id: number
  label: string
}

interface PilotOption extends Option {
  funktionen: string[]
  aktiv: boolean
}

interface Optionen {
  piloten: PilotOption[]
  drohnen: Option[]
  akkus: Option[]
}

type Format = 'pdf' | 'csv'

interface ExportArt {
  key: 'pilot' | 'drohne' | 'akku'
  titel: string
  beschreibung: string
  auswahlLabel: string
  platzhalter: string
  pfad: (id: string) => string
}

const EXPORTE: ExportArt[] = [
  {
    key: 'pilot',
    titel: 'Flugbuch Pilot',
    beschreibung: 'Persönliches Flugbuch eines Fernpiloten mit allen Flügen als Pilot oder Co-Pilot.',
    auswahlLabel: 'Benutzer (Pilot / Co-Pilot)',
    platzhalter: '— Pilot wählen —',
    pfad: id => `/export/flugbuch/pilot/${id}`,
  },
  {
    key: 'drohne',
    titel: 'Flugbuch Drohne',
    beschreibung: 'Gerätespezifisches Flugbuch: alle Flüge des gewählten Fluggeräts. Ersetzt das Papier-Flugbuch der Drohne.',
    auswahlLabel: 'Drohne',
    platzhalter: '— Drohne wählen —',
    pfad: id => `/export/flugbuch/drohne/${id}`,
  },
  {
    key: 'akku',
    titel: 'Wartungsbuch Akku',
    beschreibung: 'Stammdaten, Einsätze und Wartungseinträge (Ladezyklen, Prüfungen) des Akkus.',
    auswahlLabel: 'Akku',
    platzhalter: '— Akku wählen —',
    pfad: id => `/export/akkubuch/${id}`,
  },
]

function funktionLabel(f: string[]) {
  return f.map(x => (x === 'CO_PILOT' ? 'Co-Pilot' : 'Pilot')).sort().join(', ')
}

function dateinameAusHeader(header: string | undefined, fallback: string) {
  const m = header?.match(/filename="?([^";]+)"?/)
  return m ? m[1] : fallback
}

async function fehlerText(e: any): Promise<string> {
  const data = e?.response?.data
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.detail || 'Export fehlgeschlagen'
    } catch {
      return 'Export fehlgeschlagen'
    }
  }
  return data?.detail || e?.message || 'Export fehlgeschlagen'
}

interface FlugKurz { pilot_id?: number | null; co_pilot_id?: number | null; drohne_id?: number | null; akku_id?: number | null; startzeit?: string | null }

/** „TT.MM.JJJJ“ -> ISO „JJJJ-MM-TT“; leer = '' (kein Limit), ungültig = null */
function deZuIso(t: string): string | null {
  const s = t.trim()
  if (!s) return ''
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (!m) return null
  const d = Number(m[1]), mo = Number(m[2]), j = Number(m[3])
  const dt = new Date(j, mo - 1, d)
  if (dt.getFullYear() !== j || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return `${j}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
const jahrDe = (j: number) => ({ von: `01.01.${j}`, bis: `31.12.${j}` })
const isoLokal = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('sv-SE') : '')

function ExportKarte({ art, optionen, fluege, vorbelegt }: { art: ExportArt; optionen: { value: string; label: string }[]; fluege: FlugKurz[] | null; vorbelegt?: string }) {
  const [auswahl, setAuswahl] = useState(vorbelegt ?? '')
  const [vonText, setVonText] = useState('')
  const [bisText, setBisText] = useState('')
  const vonIso = deZuIso(vonText), bisIso = deZuIso(bisText)
  const von = vonIso ?? '', bis = bisIso ?? ''
  const formatFehler = vonIso === null || bisIso === null ? 'Datum bitte als TT.MM.JJJJ eingeben' : null
  const jahr = new Date().getFullYear()
  const setzeZeitraum = (z: { von: string; bis: string }) => { setVonText(z.von); setBisText(z.bis) }
  const anzahl = useMemo(() => {
    if (!fluege || !auswahl || formatFehler) return null
    const id = Number(auswahl)
    return fluege.filter(f => {
      const passt = art.key === 'pilot' ? f.pilot_id === id || f.co_pilot_id === id : art.key === 'drohne' ? f.drohne_id === id : f.akku_id === id
      if (!passt) return false
      const t = isoLokal(f.startzeit)
      return (!von || (!!t && t >= von)) && (!bis || (!!t && t <= bis))
    }).length
  }, [fluege, auswahl, von, bis, formatFehler, art.key])
  const [laeuft, setLaeuft] = useState<Format | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [erfolg, setErfolg] = useState<string | null>(null)

  const zeitraumFehler = formatFehler ?? (von && bis && von > bis ? '„Von“ darf nicht nach „Bis“ liegen' : null)

  async function exportieren(format: Format) {
    if (!auswahl || zeitraumFehler) return
    setLaeuft(format); setFehler(null); setErfolg(null)
    try {
      const params: Record<string, string> = { format }
      if (von) params.von = von
      if (bis) params.bis = bis
      const res = await api.get(art.pfad(auswahl), { params, responseType: 'blob' })
      const name = dateinameAusHeader(res.headers?.['content-disposition'], `${art.key}.${format}`)
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setErfolg(`${name} heruntergeladen`)
    } catch (e: any) {
      setFehler(await fehlerText(e))
    } finally {
      setLaeuft(null)
    }
  }

  return (
    <Card>
      <h2 className="font-semibold text-dark-text">{art.titel}</h2>
      <p className="mb-4 text-sm text-dark-text-muted">{art.beschreibung}</p>

      <div className="space-y-4">
        <div>
          <label className="label">{art.auswahlLabel}</label>
          <Select
            value={auswahl}
            onChange={e => { setAuswahl(e.target.value); setErfolg(null); setFehler(null) }}
            options={optionen}
            placeholder={optionen.length ? art.platzhalter : '— keine Einträge vorhanden —'}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Von</label>
            <Input inputMode="numeric" placeholder="TT.MM.JJJJ" value={vonText} onChange={e => setVonText(e.target.value)} />
          </div>
          <div>
            <label className="label">Bis</label>
            <Input inputMode="numeric" placeholder="TT.MM.JJJJ" value={bisText} onChange={e => setBisText(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {([['Dieses Jahr', jahrDe(jahr)], ['Letztes Jahr', jahrDe(jahr - 1)], ['Alle', { von: '', bis: '' }]] as const).map(([l, z]) => (
            <button key={l} type="button" onClick={() => setzeZeitraum(z)}
              className={`min-h-[40px] rounded-full border px-3.5 text-sm transition-colors ${vonText === z.von && bisText === z.bis ? 'border-primary-500/60 bg-primary-500/15 text-dark-text' : 'border-dark-border text-dark-text-muted hover:bg-dark-bg-tertiary/60 hover:text-dark-text'}`}>{l}</button>
          ))}
        </div>
        <p className="text-sm" role="status" aria-live="polite">
          {anzahl == null ? <span className="text-dark-text-subtle">{auswahl ? (fluege ? 'Zeitraum prüfen …' : 'Zähle Flüge …') : 'Bitte eine Auswahl treffen. Ohne Zeitraum werden alle Flüge exportiert.'}</span>
            : <><span className="font-semibold text-dark-text">{anzahl}</span> <span className="text-dark-text-muted">{anzahl === 1 ? 'Flug' : 'Flüge'} im Zeitraum</span></>}
        </p>

        {(zeitraumFehler || fehler) && (
          <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-3 py-2 text-sm text-red-200">
            {zeitraumFehler || fehler}
          </div>
        )}
        {erfolg && (
          <div className="rounded-lg border border-green-700/50 bg-green-900/20 px-3 py-2 text-sm text-green-300">{erfolg}</div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={() => exportieren('csv')} disabled={!auswahl || !!zeitraumFehler || !!laeuft || anzahl === 0}>
            {laeuft === 'csv' ? 'Exportiere…' : 'CSV (Excel)'}
          </Button>
          <Button onClick={() => exportieren('pdf')} disabled={!auswahl || !!zeitraumFehler || !!laeuft || anzahl === 0}>
            {laeuft === 'pdf' ? 'Exportiere…' : 'PDF (Flugbuch)'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

export function ExportPage() {
  const [optionen, setOptionen] = useState<Optionen | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [fluege, setFluege] = useState<FlugKurz[] | null>(null)
  const [suchParams] = useSearchParams()
  const vorArt = suchParams.get('art'), vorId = suchParams.get('id') ?? undefined

  useEffect(() => {
    api.get('/fluege', { params: { size: 5000 } })
      .then(res => setFluege(((res.data as any)?.items ?? []) as FlugKurz[]))
      .catch(() => setFluege([]))
  }, [])

  useEffect(() => {
    api.get('/export/optionen')
      .then(res => setOptionen(res.data as Optionen))
      .catch(e => setFehler(e?.response?.data?.detail || 'Auswahllisten konnten nicht geladen werden'))
  }, [])

  const auswahl = {
    pilot: (optionen?.piloten ?? []).map(p => ({
      value: String(p.id),
      label: `${p.label} (${funktionLabel(p.funktionen)})${p.aktiv === false ? ' – inaktiv' : ''}`,
    })),
    drohne: (optionen?.drohnen ?? []).map(d => ({ value: String(d.id), label: d.label })),
    akku: (optionen?.akkus ?? []).map(a => ({ value: String(a.id), label: a.label })),
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title=""
        subtitle="Flugbücher nach EGRED 2 (Anlage VII) als PDF zum Ausdrucken und Unterschreiben oder als CSV für Excel"
      />

      <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary/30 px-4 py-3 text-sm text-dark-text-muted">
        Hinweis: Payload und Vorkommnisse werden noch nicht erfasst und bleiben im Flugbuch zum handschriftlichen Ergänzen frei.
        Ist bei einem Flug keine Flugart (VLoS/EVLoS/BVLoS) angegeben, bleibt sie zum Ankreuzen frei.
      </div>

      {fehler && (
        <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        {EXPORTE.map(art => (
          <ExportKarte key={art.key} art={art} optionen={auswahl[art.key]} fluege={fluege} vorbelegt={vorArt === art.key ? vorId : undefined} />
        ))}
      </div>

    </div>
  )
}
